import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClientContext } from '@/App';

type AppRole = 'admin' | 'moderator' | 'member_free' | 'member_paid' | 'verified_trader';
export type SubscriptionTier = 'alpha_observer' | 'alpha_participant' | 'alpha_operator';

interface TemporaryTierGrant {
  tier: SubscriptionTier;
  expires_at: string;
}

interface Profile {
  id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  city: string | null;
  state: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  subscriptionTier: SubscriptionTier | null;
  effectiveTier: SubscriptionTier | null;
  temporaryGrant: TemporaryTierGrant | null;
  isLoading: boolean;
  isDataReady: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  isPaidMember: boolean;
  isVerifiedTrader: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null; data: { user: User | null } | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to get the higher tier
function getHigherTier(tier1: SubscriptionTier | null, tier2: SubscriptionTier | null): SubscriptionTier | null {
  const tierOrder: Record<SubscriptionTier, number> = {
    alpha_observer: 1,
    alpha_participant: 2,
    alpha_operator: 3,
  };
  
  if (!tier1 && !tier2) return null;
  if (!tier1) return tier2;
  if (!tier2) return tier1;
  
  return tierOrder[tier1] >= tierOrder[tier2] ? tier1 : tier2;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier | null>(null);
  const [temporaryGrant, setTemporaryGrant] = useState<TemporaryTierGrant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  
  // Get queryClient for cache invalidation on login
  const queryClient = useQueryClientContext();

  const fetchUserData = async (userId: string) => {
    setDataFetched(false);
    try {
      // Fetch all user data in parallel for efficiency
      const [profileResult, rolesResult, subscriptionResult, tempGrantResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .single(),
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId),
        supabase
          .from('subscriptions')
          .select('tier, status')
          .eq('user_id', userId)
          .eq('status', 'active')
          .maybeSingle(), // Use maybeSingle to handle "no subscription" gracefully (returns null, not error)
        supabase
          .from('temporary_tier_grants')
          .select('tier, expires_at')
          .eq('user_id', userId)
          .gt('expires_at', new Date().toISOString())
          .maybeSingle()
      ]);

      // Batch all state updates together to prevent partial state renders
      const newProfile = profileResult.data as Profile | null;
      const newRoles = rolesResult.data?.map(r => r.role as AppRole) ?? [];
      const newTier = (subscriptionResult.data?.tier as SubscriptionTier | null) ?? null;
      const newTempGrant = tempGrantResult.data as TemporaryTierGrant | null;

      // Update all states in a single render cycle
      setProfile(newProfile);
      setRoles(newRoles);
      setSubscriptionTier(newTier);
      setTemporaryGrant(newTempGrant);
      setDataFetched(true);
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Even on error, ensure we have empty/default values set
      setProfile(null);
      setRoles([]);
      setSubscriptionTier(null);
      setTemporaryGrant(null);
      setDataFetched(true);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Clear all React Query cache on login to ensure fresh data
          if (event === 'SIGNED_IN' && queryClient) {
            console.log('[Auth] Clearing query cache on login for fresh data');
            queryClient.clear();
          }
          
          // Defer Supabase calls with setTimeout to avoid deadlock
          setTimeout(async () => {
            await fetchUserData(session.user.id);
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setSubscriptionTier(null);
          setTemporaryGrant(null);
          setDataFetched(true);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        // No session - user not logged in, safe to stop loading
        setSession(null);
        setUser(null);
        setIsLoading(false);
      }
      // If session exists, onAuthStateChange will handle it
      // Don't set loading false here to avoid race condition
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, displayName?: string) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: displayName || email.split('@')[0],
        },
      },
    });
    return { error, data: data ? { user: data.user } : null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/profile-loading`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setSubscriptionTier(null);
    setTemporaryGrant(null);
  };

  const isAdmin = roles.includes('admin');
  const isModerator = roles.includes('moderator') || isAdmin;
  
  // Calculate effective tier (higher of subscription or temporary grant)
  const effectiveTier = getHigherTier(subscriptionTier, temporaryGrant?.tier ?? null);
  
  const isPaidMember = roles.includes('member_paid') || effectiveTier !== null;
  const isVerifiedTrader = roles.includes('verified_trader');
  
  // isDataReady is true when loading is complete AND (no user OR data has been fetched)
  const isDataReady = !isLoading && (!user || dataFetched);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        subscriptionTier,
        effectiveTier,
        temporaryGrant,
        isLoading,
        isDataReady,
        isAdmin,
        isModerator,
        isPaidMember,
        isVerifiedTrader,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
